import { useContext, useEffect, useRef, useState } from "react";
import olMap from "ol/Map";
import olDrawInteraction from "ol/interaction/Draw";
import olDragPan from "ol/interaction/DragPan";
import olVectorSource from "ol/source/Vector";
import { EventsKey } from "ol/events";
import { unByKey } from "ol/Observable";
import { getArea } from "ol/sphere";

import { OLParser } from "../JSTS/OL";
import MapContext from "../map/MapContext";

export interface PolygonProps {
  source: olVectorSource;
  active: boolean;
  freehand: boolean;
  onDrawEnd?: (polygon: jsts.geom.Polygon) => void;
  onDrawing?: (areaSqKm: number | undefined) => void;
}

export const DrawPolygon = ({
  source,
  active,
  onDrawEnd,
  onDrawing,
  freehand,
}: PolygonProps) => {
  const [interaction, setInteraction] = useState<olDrawInteraction | null>(
    null
  );
  const map = useContext(MapContext) as olMap;
  const onDrawEndRef = useRef(onDrawEnd);
  onDrawEndRef.current = onDrawEnd;
  const onDrawingRef = useRef(onDrawing);
  onDrawingRef.current = onDrawing;

  useEffect(() => {
    if (!map) return () => {};

    const interaction = new olDrawInteraction({
      source,
      type: "Polygon",
      freehand
    });

    interaction.setActive(active);

    let geometryChangeKey: EventsKey | undefined;
    interaction.on("drawstart", (e) => {
      const geometry = e.feature.getGeometry();
      const reportArea = () => {
        const area = geometry ? getArea(geometry, { projection: "EPSG:3857" }) : 0;
        onDrawingRef.current?.(area / 1_000_000);
      };
      reportArea();
      geometryChangeKey = geometry?.on("change", reportArea);
    });
    interaction.on(["drawend", "drawabort"], () => {
      if (geometryChangeKey) unByKey(geometryChangeKey);
      onDrawingRef.current?.(undefined);
    });

    interaction.on("drawend", (e) => {
      if (!onDrawEndRef.current) return;
      const jstsGeom = OLParser.read(
        e.feature.getGeometry()
      ) as jsts.geom.Polygon;
      onDrawEndRef.current(jstsGeom);
    });
    map.addInteraction(interaction);
    setInteraction(interaction);

    return () => {
      if (!map) return;
      map.removeInteraction(interaction);
      setInteraction(null);
      onDrawingRef.current?.(undefined);
    };
  }, [map, source, freehand]);

  useEffect(() => {
    if (!interaction) return;
    interaction.setActive(active);
  }, [active]);

  useEffect(() => {
    if (!map || !freehand) return;
    map.getInteractions().forEach((i) => {
      if (i instanceof olDragPan) i.setActive(!active);
    });
    return () => {
      map.getInteractions().forEach((i) => {
        if (i instanceof olDragPan) i.setActive(true);
      });
    };
  }, [map, freehand, active]);

  return null;
};
